'use client'

import { useCookieValue } from '@react-hookz/web/useCookieValue/index.js'

import { useEffect, useState } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import type { CoreUser } from '../types'

import { BrowserClient } from '../ampli'
import { isDev, isPreview } from '../helpers'
import useRole from './useRole'
import useUser from './useUser'

export default function useAmplitude({ waitForUser = true } = {}) {
  const { isLoading: isUserLoading, user } = useUser<CoreUser>()
  const { isStaff } = useRole()

  const [gdprConsent] = useCookieValue('gdpr-consent')

  const [isInitialized, setIsInitialized] = useState(false)
  const [amplitude, setAmplitude] = useState<BrowserClient | null>(null)

  useEffect(() => {
    const initAmplitude = async () => {
      const amplitudeApiKey =
        process.env.AMPLITUDE_API_KEY ||
        process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY
      if (isEmpty(amplitudeApiKey)) {
        throw new Error('No Amplitude API key provided!')
      }

      const amplitudeSDK = await import('@amplitude/analytics-browser')
      await amplitudeSDK.init(amplitudeApiKey, user?.uuid, {
        defaultTracking: {
          attribution: true,
          fileDownloads: true,
          formInteractions: false,
          pageViews: false,
          sessions: true,
        },
        logLevel: amplitudeSDK.Types.LogLevel.Warn,
        serverZone: 'EU',
      })

      if (isStaff) {
        amplitudeSDK.setOptOut(!isDev() && !isPreview())
      }

      if (!isEmpty(user?.uuid)) {
        amplitudeSDK.identify(new amplitudeSDK.Identify(), {
          user_id: user?.uuid,
        })
      }

      setAmplitude(amplitudeSDK)
      setIsInitialized(true)
    }

    if (!waitForUser || !isUserLoading) {
      if ((!isInitialized || amplitude === null) && gdprConsent === 'true') {
        initAmplitude()
      } else if (!isEmpty(user?.uuid) && isInitialized && amplitude !== null) {
        amplitude.setUserId(user?.uuid)

        if (isStaff) {
          amplitude?.setOptOut(!isDev() && !isPreview())
        } else {
          amplitude?.setOptOut(false)
        }
      }
    }
  }, [
    amplitude,
    isInitialized,
    isUserLoading,
    isStaff,
    user,
    waitForUser,
    gdprConsent,
  ])

  return { amplitude: amplitude ?? null, isInitialized }
}
